import React, {
  createContext, JSX, ReactNode, useCallback,
  useContext, useEffect, useRef, useState
} from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import logger from 'logger'

const log = logger.extend('socket-token')

const STORAGE_KEY = 'stream-tools.socket-token.v1'

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000

const { VITE_TOKEN_EXCHANGE_URL: tokenExchangeUrl } = import.meta.env

interface SocketTokenValue {
  token: string
  expiresAt: string
}

interface StoredSocketToken extends SocketTokenValue {
  owner: string
}

export interface SocketTokenContextType {
  socketToken: string | undefined
  expiresAt: string | undefined
  refresh: () => Promise<void>
}

const defaultContext: SocketTokenContextType = {
  socketToken: undefined,
  expiresAt: undefined,
  refresh: async () => {}
}

const SocketTokenContext = createContext<SocketTokenContextType>(defaultContext)
SocketTokenContext.displayName = 'Socket Token Context'

const loadFromStorage = (owner: string): SocketTokenValue | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null || raw === '') return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.owner !== 'string' || parsed.owner === '' || parsed.owner !== owner) {
      clearStorage()
      return null
    }
    if (typeof parsed?.token !== 'string') return null
    if (typeof parsed?.expiresAt !== 'string') return null
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) return null
    return parsed
  } catch (err) {
    log({ loadFromStorage: 'failed', err: err instanceof Error ? err.name : 'unknown' })
    return null
  }
}

const passSubject = (token: string): string | null => {
  try {
    const [, payload] = token.split('.')
    if (payload == null || payload === '') return null
    const claims = JSON.parse(globalThis.atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof claims?.sub === 'string' ? claims.sub : null
  } catch {
    return null
  }
}

const saveToStorage = (value: SocketTokenValue, owner: string): void => {
  if (passSubject(value.token) !== owner) {
    log({ saveToStorage: 'skipped — pass does not name this user' })
    return
  }
  try {
    const stored: StoredSocketToken = { ...value, owner }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch (err) {
    log({ saveToStorage: 'failed', err: err instanceof Error ? err.name : 'unknown' })
  }
}

const clearStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (err) {
    log({ clearStorage: 'failed', err: err instanceof Error ? err.name : 'unknown' })
  }
}

export const clearStoredSocketToken = (): void => {
  clearStorage()
}

export const SocketTokenProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0()
  const owner = user?.sub
  const [state, setState] = useState<SocketTokenValue | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined)
  const inflightRef = useRef<Promise<void> | null>(null)

  const fetchToken = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || owner == null || owner === '') return
    if (typeof tokenExchangeUrl !== 'string' || tokenExchangeUrl === '') {
      log({ fetchToken: 'skipped, error' })
      return
    }
    if (inflightRef.current != null) {
      return await inflightRef.current
    }
    inflightRef.current = (async () => {
      try {
        const auth0Token = await getAccessTokenSilently()
        const res = await fetch(`${tokenExchangeUrl}/streamToolsSocketToken`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${auth0Token}` },
          signal: AbortSignal.timeout(8000)
        })
        if (!res.ok) {
          log({ fetchToken: 'http error', status: res.status })
          return
        }
        const data = await res.json()
        if (typeof data?.appsyncToken !== 'string') {
          log({ fetchToken: 'bad response shape', keys: Object.keys(data ?? {}) })
          return
        }
        const value: SocketTokenValue = {
          token: data.appsyncToken,
          expiresAt: data.expiresAt
        }
        setState(value)
        saveToStorage(value, owner)
        log({ fetchToken: 'ok', expiresAt: value.expiresAt })
      } catch (err) {
        log({ fetchToken: 'failed', err: err instanceof Error ? err.name : 'unknown' })
      } finally {
        inflightRef.current = null
      }
    })()
    return await inflightRef.current
  }, [isAuthenticated, owner, getAccessTokenSilently])

  useEffect(() => {
    if (!isAuthenticated) {
      setState(null)
      clearStorage()
      return
    }
    if (owner == null || owner === '') return
    const cached = loadFromStorage(owner)
    if (cached !== null) {
      const remaining = new Date(cached.expiresAt).getTime() - Date.now()
      if (remaining > REFRESH_BEFORE_EXPIRY_MS) {
        setState(cached)
        return
      }
    }
    void fetchToken()
  }, [isAuthenticated, owner, fetchToken])

  useEffect(() => {
    if (state == null) return
    const expiryMs = new Date(state.expiresAt).getTime()
    const refreshAt = expiryMs - REFRESH_BEFORE_EXPIRY_MS
    const delay = Math.max(0, refreshAt - Date.now())
    log({ scheduleRefresh: 'set', inMs: delay })
    refreshTimerRef.current = globalThis.setTimeout(() => {
      void fetchToken()
    }, delay)
    return () => {
      if (refreshTimerRef.current !== undefined) {
        globalThis.clearTimeout(refreshTimerRef.current)
      }
    }
  }, [state?.expiresAt, fetchToken])

  return (
    <SocketTokenContext.Provider value={{
      socketToken: state?.token,
      expiresAt: state?.expiresAt,
      refresh: fetchToken
    }}
    >
      {children}
    </SocketTokenContext.Provider>
  )
}

export const useSocketToken = (): SocketTokenContextType =>
  useContext(SocketTokenContext)
